<template>
  <section class="digital-certificates-page">
    <div class="dc-hero">
      <div>
        <span>ICP-Brasil</span>
        <h1>Certificados digitais</h1>
        <p>
          Cadastre certificados A1 da empresa ou do usuário para preparar o
          Certificador para assinaturas digitais com credenciais próprias.
        </p>
      </div>

      <div class="dc-hero-status">
        <small>Status</small>
        <strong>Base A1 ativa</strong>
        <p>Upload, validação e armazenamento seguro preparados.</p>
      </div>
    </div>

    <div class="dc-grid">
      <section class="dc-upload-card">
        <div class="dc-card-header">
          <div>
            <span>Novo certificado</span>
            <strong>Upload A1</strong>
            <p>
              Envie um arquivo .pfx ou .p12. A senha será usada apenas para
              validação e armazenada criptografada no backend.
            </p>
          </div>
        </div>

        <form class="dc-form" @submit.prevent="submitCertificate">
          <div class="dc-field">
            <label>Proprietário</label>

            <div class="dc-owner-grid">
              <button
                type="button"
                :class="{ active: form.ownerType === 'company' }"
                @click="form.ownerType = 'company'"
              >
                <span>Empresa</span>
                <small>Assinatura pelo CNPJ/cliente</small>
              </button>

              <button
                type="button"
                :class="{ active: form.ownerType === 'user' }"
                @click="form.ownerType = 'user'"
              >
                <span>Usuário</span>
                <small>Assinatura pessoal/e-CPF</small>
              </button>
            </div>
          </div>

          <div class="dc-field">
            <label>Nome de identificação</label>
            <input
              v-model="form.name"
              type="text"
              placeholder="Ex.: Certificado da empresa"
            />
          </div>

          <div class="dc-field">
            <label>Arquivo do certificado</label>

            <label
              class="dc-file-drop"
              :class="{ selected: form.file }"
              for="digital-certificate-file"
            >
              <input
                id="digital-certificate-file"
                type="file"
                accept=".pfx,.p12"
                @change="handleFileChange"
              />

              <span>{{
                form.file ? "Arquivo selecionado" : "Selecionar .pfx/.p12"
              }}</span>
              <strong>{{
                form.file?.name || "Nenhum arquivo selecionado"
              }}</strong>
              <small>Máximo recomendado: 5MB</small>
            </label>
          </div>

          <div class="dc-field">
            <label>Senha do certificado</label>

            <div class="dc-password-wrap">
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Informe a senha do .pfx/.p12"
              />

              <button type="button" @click="showPassword = !showPassword">
                {{ showPassword ? "Ocultar" : "Ver" }}
              </button>
            </div>
          </div>

          <div v-if="errorMessage" class="dc-alert error">
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="dc-alert success">
            {{ successMessage }}
          </div>

          <button class="dc-submit" type="submit" :disabled="isSubmitting">
            {{
              isSubmitting
                ? "Validando certificado..."
                : "Validar e salvar certificado"
            }}
          </button>
        </form>
      </section>

      <section class="dc-list-card">
        <div class="dc-card-header horizontal">
          <div>
            <span>Banco de certificados</span>
            <strong>Certificados cadastrados</strong>
          </div>

          <button type="button" @click="loadCertificates" :disabled="isLoading">
            Atualizar
          </button>
        </div>

        <div class="dc-summary">
          <article>
            <span>Total</span>
            <strong>{{ certificates.length }}</strong>
          </article>

          <article>
            <span>Ativos</span>
            <strong>{{ activeCount }}</strong>
          </article>

          <article>
            <span>Vencidos</span>
            <strong>{{ expiredCount }}</strong>
          </article>
        </div>

        <div v-if="isLoading" class="dc-empty">Carregando certificados...</div>

        <div v-else-if="!certificates.length" class="dc-empty">
          <strong>Nenhum certificado cadastrado ainda.</strong>
          <p>
            Use o formulário ao lado para validar e armazenar um certificado A1.
          </p>
        </div>

        <div v-else class="dc-list">
          <article
            v-for="certificate in certificates"
            :key="certificate.id"
            class="dc-certificate-item"
            :class="{ expired: isExpired(certificate) }"
          >
            <div class="dc-certificate-top">
              <div>
                <span>{{ ownerLabel(certificate.owner_type) }}</span>
                <strong>{{ certificate.name || "Certificado digital" }}</strong>
              </div>

              <div class="dc-certificate-actions">
                <small :class="statusClass(certificate)">
                  {{ statusLabel(certificate) }}
                </small>

                <button
                  type="button"
                  class="dc-delete-button"
                  :disabled="deletingId === certificate.id"
                  @click="removeCertificate(certificate)"
                >
                  {{ deletingId === certificate.id ? "..." : "Remover" }}
                </button>
              </div>

              <!-- <small :class="statusClass(certificate)">
                {{ statusLabel(certificate) }}
              </small> -->
            </div>

            <dl>
              <div>
                <dt>Tipo</dt>
                <dd>{{ certificate.type || "A1_PFX" }}</dd>
              </div>

              <div>
                <dt>Validade</dt>
                <dd>{{ formatDate(certificate.valid_until) }}</dd>
              </div>

              <div>
                <dt>Titular</dt>
                <dd>
                  {{ extractCommonName(certificate.certificate_subject) }}
                </dd>
              </div>

              <div>
                <dt>Emissor</dt>
                <dd>{{ extractCommonName(certificate.certificate_issuer) }}</dd>
              </div>

              <div class="full">
                <dt>Fingerprint SHA-256</dt>
                <dd>{{ shortFingerprint(certificate.fingerprint_sha256) }}</dd>
              </div>
            </dl>
          </article>
        </div>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import {
  deleteDigitalCertificate,
  listDigitalCertificates,
  uploadDigitalCertificate,
} from "@/services/digitalCertificateService";

const certificates = ref([]);
const isLoading = ref(false);
const isSubmitting = ref(false);
const showPassword = ref(false);
const errorMessage = ref("");
const successMessage = ref("");
const deletingId = ref(null);

const form = reactive({
  ownerType: "company",
  name: "",
  password: "",
  file: null,
});

const activeCount = computed(() => {
  return certificates.value.filter((certificate) => {
    return !isExpired(certificate) && certificate.status === "active";
  }).length;
});

const expiredCount = computed(() => {
  return certificates.value.filter((certificate) => isExpired(certificate))
    .length;
});

onMounted(() => {
  loadCertificates();
});

async function loadCertificates() {
  isLoading.value = true;
  errorMessage.value = "";

  try {
    certificates.value = await listDigitalCertificates();
  } catch (error) {
    errorMessage.value =
      error.message || "Não foi possível carregar os certificados digitais.";
  } finally {
    isLoading.value = false;
  }
}

function handleFileChange(event) {
  const file = event.target.files?.[0];

  form.file = file || null;
  errorMessage.value = "";
  successMessage.value = "";
}

async function submitCertificate() {
  errorMessage.value = "";
  successMessage.value = "";

  if (!form.file) {
    errorMessage.value = "Selecione um arquivo .pfx ou .p12.";
    return;
  }

  if (!form.password) {
    errorMessage.value = "Informe a senha do certificado digital.";
    return;
  }

  isSubmitting.value = true;

  try {
    const certificate = await uploadDigitalCertificate({
      ownerType: form.ownerType,
      name: form.name,
      password: form.password,
      file: form.file,
    });

    certificates.value = [certificate, ...certificates.value];

    successMessage.value = "Certificado validado e armazenado com sucesso.";

    form.name = "";
    form.password = "";
    form.file = null;
    showPassword.value = false;

    const input = document.getElementById("digital-certificate-file");

    if (input) {
      input.value = "";
    }
  } catch (error) {
    errorMessage.value =
      error.message || "Não foi possível validar o certificado digital.";
  } finally {
    isSubmitting.value = false;
  }
}

async function removeCertificate(certificate) {
  const confirmed = confirm(
    `Deseja remover o certificado "${
      certificate.name || "Certificado digital"
    }"?`
  );

  if (!confirmed) {
    return;
  }

  deletingId.value = certificate.id;
  errorMessage.value = "";
  successMessage.value = "";

  try {
    await deleteDigitalCertificate(certificate.id);

    certificates.value = certificates.value.filter((item) => {
      return Number(item.id) !== Number(certificate.id);
    });

    successMessage.value = "Certificado digital removido com sucesso.";
  } catch (error) {
    errorMessage.value =
      error.message || "Não foi possível remover o certificado digital.";
  } finally {
    deletingId.value = null;
  }
}

function ownerLabel(ownerType) {
  const value = normalizeValue(ownerType);

  if (value === "company") {
    return "Empresa";
  }

  if (value === "user") {
    return "Usuário";
  }

  if (value === "platform") {
    return "Plataforma";
  }

  return "Certificado";
}

function statusLabel(certificate) {
  if (isExpired(certificate)) {
    return "Vencido";
  }

  if (certificate.validation_status === "valid") {
    return "Validado";
  }

  if (certificate.status === "active") {
    return "Ativo";
  }

  return certificate.status || "Pendente";
}

function statusClass(certificate) {
  if (isExpired(certificate)) {
    return "expired";
  }

  if (certificate.validation_status === "valid") {
    return "valid";
  }

  return "neutral";
}

function isExpired(certificate) {
  if (!certificate.valid_until) {
    return false;
  }

  return new Date(certificate.valid_until) < new Date();
}

function formatDate(value) {
  if (!value) {
    return "Não informado";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));
}

function extractCommonName(distinguishedName) {
  if (!distinguishedName) {
    return "Não informado";
  }

  const match = distinguishedName.match(/CN=([^,]+)/);

  return match?.[1]?.trim() || distinguishedName;
}

function shortFingerprint(value) {
  if (!value) {
    return "Não informado";
  }

  if (value.length <= 18) {
    return value;
  }

  return `${value.slice(0, 10)}...${value.slice(-8)}`;
}

function normalizeValue(value) {
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }

  return value;
}
</script>

<style scoped>
.digital-certificates-page {
  max-width: 1180px;
  margin: 0 auto;
}

.dc-hero,
.dc-upload-card,
.dc-list-card {
  border: 1px solid rgba(214, 222, 234, 0.92);
  background: radial-gradient(
      circle at top right,
      rgba(255, 148, 23, 0.14),
      transparent 22rem
    ),
    rgba(255, 255, 255, 0.88);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.09);
}

.dc-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.4rem;
  padding: 1.65rem 1.85rem;
  border-radius: 30px;
}

.dc-hero span,
.dc-card-header span {
  color: #f97316;
  font-size: 0.68rem;
  font-family: "XoloniumBold", sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.dc-hero h1 {
  margin: 0.45rem 0;
  color: #0f172a;
  font-size: clamp(1.85rem, 3.2vw, 2.75rem);
  line-height: 0.98;
  letter-spacing: -0.045em;
  font-family: "XoloniumBold", sans-serif;
}

.dc-hero p,
.dc-card-header p {
  max-width: 760px;
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.7;
}

.dc-hero-status {
  min-width: 230px;
  align-self: flex-start;
  padding: 1rem;
  border-radius: 22px;
  color: #64748b;
  background: radial-gradient(
      circle at top right,
      rgba(255, 148, 23, 0.11),
      transparent 22rem
    ),
    rgba(255, 255, 255, 0.9);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
}

.dc-hero-status small {
  color: #f97316;
  font-size: 0.68rem;
  text-transform: uppercase;
}

.dc-hero-status strong {
  display: block;
  margin-top: 0.25rem;
  font-family: "XoloniumBold", sans-serif;
}

.dc-grid {
  display: grid;
  grid-template-columns: 420px minmax(0, 1fr);
  gap: 1rem;
  margin-top: 1rem;
}

.dc-upload-card,
.dc-list-card {
  border-radius: 28px;
  padding: 1.25rem;
}

.dc-card-header.horizontal {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.dc-card-header strong {
  display: block;
  margin-top: 0.3rem;
  color: #111827;
  font-size: 1.05rem;
  font-family: "XoloniumBold", sans-serif;
}

.dc-card-header button {
  min-height: 38px;
  border: 1px solid #d6deea;
  border-radius: 13px;
  padding: 0 0.85rem;
  background: #ffffff;
  color: #334155;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  cursor: pointer;
}

.dc-form {
  display: grid;
  gap: 0.9rem;
  margin-top: 1.1rem;
}

.dc-field label {
  display: block;
  margin-bottom: 0.42rem;
  color: #334155;
  font-size: 0.68rem;
  font-family: "XoloniumBold", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.dc-field input {
  width: 100%;
  height: 50px;
  border: 1px solid #d6deea;
  border-radius: 15px;
  padding: 0 0.85rem;
  color: #111827;
  background: rgba(248, 250, 252, 0.92);
  outline: none;
  font-size: 0.82rem;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.04);
}

.dc-field input:focus {
  border-color: rgba(249, 115, 22, 0.65);
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.12),
    0 14px 32px rgba(15, 23, 42, 0.08);
}

.dc-owner-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
}

.dc-owner-grid button {
  min-height: 72px;
  border: 1px solid #d6deea;
  border-radius: 18px;
  padding: 0.75rem;
  text-align: left;
  background: #ffffff;
  cursor: pointer;
  transition: 0.18s ease;
}

.dc-owner-grid button.active {
  background: #fff7ed;
  border-color: rgba(249, 115, 22, 0.45);
  box-shadow: 0 14px 30px rgba(249, 115, 22, 0.1);
}

.dc-owner-grid span {
  display: block;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.78rem;
}

.dc-owner-grid small {
  display: block;
  margin-top: 0.35rem;
  color: #64748b;
  font-size: 0.62rem;
  line-height: 1.35;
}

.dc-file-drop {
  min-height: 118px;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 20px;
  background: #f8fafc;
  cursor: pointer;
  transition: 0.18s ease;
}

.dc-file-drop.selected {
  border-color: rgba(34, 197, 94, 0.45);
  background: #f0fdf4;
}

.dc-file-drop input {
  display: none;
}

.dc-file-drop span {
  color: #f97316;
  font-size: 0.68rem;
  font-family: "XoloniumBold", sans-serif;
  text-transform: uppercase;
}

.dc-file-drop strong {
  display: block;
  margin-top: 0.3rem;
  color: #111827;
  font-size: 0.78rem;
  word-break: break-word;
}

.dc-file-drop small {
  color: #64748b;
  font-size: 0.62rem;
}

.dc-password-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 78px;
  gap: 0.5rem;
}

.dc-password-wrap button {
  border: 0;
  border-radius: 15px;
  color: #111827;
  background: #e2e8f0;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  cursor: pointer;
}

.dc-submit {
  min-height: 50px;
  border: 0;
  border-radius: 16px;
  color: #ffffff;
  background: linear-gradient(135deg, #ffb020, #ff7300);
  box-shadow: 0 16px 34px rgba(255, 115, 0, 0.22);
  font-family: "XoloniumBold", sans-serif;
  cursor: pointer;
}

.dc-submit:disabled {
  opacity: 0.58;
  cursor: not-allowed;
}

.dc-alert {
  padding: 0.82rem;
  border-radius: 16px;
  font-size: 0.72rem;
  line-height: 1.55;
}

.dc-alert.error {
  color: #991b1b;
  background: #fef2f2;
  border: 1px solid rgba(239, 68, 68, 0.22);
}

.dc-alert.success {
  color: #166534;
  background: #f0fdf4;
  border: 1px solid rgba(34, 197, 94, 0.22);
}

.dc-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 1rem;
}

.dc-summary article {
  padding: 0.85rem;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.dc-summary span {
  display: block;
  color: #64748b;
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.dc-summary strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1.05rem;
}

.dc-empty {
  margin-top: 1rem;
  padding: 1.2rem;
  border-radius: 20px;
  color: #64748b;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
}

.dc-empty strong {
  display: block;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
}

.dc-list {
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
}

.dc-certificate-item {
  padding: 1rem;
  border-radius: 22px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.dc-certificate-item.expired {
  border-color: rgba(239, 68, 68, 0.28);
  background: #fff7f7;
}

.dc-certificate-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.dc-certificate-top span {
  display: block;
  color: #f97316;
  font-size: 0.62rem;
  font-family: "XoloniumBold", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.dc-certificate-top strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.9rem;
}

.dc-certificate-top small {
  padding: 0.32rem 0.58rem;
  border-radius: 999px;
  font-size: 0.62rem;
  white-space: nowrap;
}

.dc-certificate-top small.valid {
  color: #166534;
  background: #dcfce7;
}

.dc-certificate-top small.expired {
  color: #991b1b;
  background: #fee2e2;
}

.dc-certificate-top small.neutral {
  color: #334155;
  background: #e2e8f0;
}

.dc-certificate-item dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
  margin: 1rem 0 0;
}

.dc-certificate-item dl div.full {
  grid-column: 1 / -1;
}

.dc-certificate-item dt {
  color: #94a3b8;
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.dc-certificate-item dd {
  margin: 0.22rem 0 0;
  color: #334155;
  font-size: 0.72rem;
  line-height: 1.45;
  word-break: break-word;
}

.dc-certificate-actions {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.dc-delete-button {
  min-height: 30px;
  border: 0;
  border-radius: 999px;
  padding: 0 0.65rem;
  color: #b91c1c;
  background: #fee2e2;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.58rem;
  cursor: pointer;
}

.dc-delete-button:hover:not(:disabled) {
  background: #fecaca;
}

.dc-delete-button:disabled {
  opacity: 0.58;
  cursor: not-allowed;
}

@media (max-width: 1080px) {
  .dc-grid {
    grid-template-columns: 1fr;
  }

  .dc-hero {
    flex-direction: column;
  }

  .dc-hero-status {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .dc-hero,
  .dc-upload-card,
  .dc-list-card {
    padding: 1rem;
    border-radius: 22px;
  }

  .dc-owner-grid,
  .dc-summary,
  .dc-certificate-item dl {
    grid-template-columns: 1fr;
  }

  .dc-password-wrap {
    grid-template-columns: 1fr;
  }

  .dc-password-wrap button {
    min-height: 44px;
  }
}
</style>
